import * as yup from "yup";
import axios from "axios";
import { endpoints } from "../../../api/endpoint";
import { ROLES } from "../../../constants/roles";
import {
  INVOICE_VALUE_CEILING,
  DRIVER_AGE_MIN,
  DRIVER_AGE_MAX,
} from "../config/emiConfig";

const PHONE_REGEX = /^\d{10}$/;
const ALPHA_SPACE_REGEX = /^[A-Za-z\s]+$/;

export const driverInformationSchema = yup.object({
  driverName: yup.string().trim().required("This field is required"),

  phoneNumber: yup
    .string()
    .trim()
    .required("This field is required")
    .matches(PHONE_REGEX, "Phone number must be exactly 10 digits")
    .test(
      "phone-unique",
      "This phone number is already in use",
      function (value) {
        if (!PHONE_REGEX.test(value || "")) return true;
        return this.options.context?.phoneNumberTaken !== true;
      },
    ),

  age: yup.string().trim().required("This field is required"),

  city: yup.string().trim().required("This field is required"),

  invoiceValue: yup
    .string()
    .trim()
    .required("This field is required")
    .test(
      "is-number",
      "Enter a valid number",
      (value) => !value || !Number.isNaN(Number(value)),
    )
    .test(
      "is-positive",
      "Invoice value must be greater than 0",
      (value) => !value || Number(value) > 0,
    )
    .test(
      "invoice-ceiling",
      `Invoice value cannot exceed ₹${INVOICE_VALUE_CEILING.toLocaleString("en-IN")}`,
      (value) => !value || Number(value) <= INVOICE_VALUE_CEILING,
    ),

  downpayment: yup
    .string()
    .trim()
    .required("Please select a downpayment amount"),

  emiTenure: yup.string().trim().required("Please select a tenure"),

  vehicleFinanced: yup
    .string()
    .oneOf(["Yes", "No"], "Please select an option")
    .required("Please select an option"),

  vehicleFinancierDetails: yup.object().when("vehicleFinanced", {
    is: "Yes",
    then: (schema) =>
      schema.shape({
        name: yup.string().trim().required("This field is required"),
        phoneNumber: yup
          .string()
          .trim()
          .required("This field is required")
          .matches(PHONE_REGEX, "Phone number must be exactly 10 digits"),
        address: yup.string().trim().required("This field is required"),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const assetAllocationSchema = yup.object({
  batteryType: yup.string().trim().required("This field is required"),
  batteryImei: yup.string().trim().required("This field is required"),
  chargerId: yup.string().trim().required("This field is required"),
});

const documentUploadDealerFields = {
  aadhaarFrontUrl: yup.mixed().required("This document is required"),
  aadhaarBackUrl: yup.mixed().required("This document is required"),
  panCardUrl: yup.mixed().required("This document is required"),
  rcFrontUrl: yup.mixed().required("This document is required"),
  rcBackUrl: yup.mixed().required("This document is required"),
  addressProofUrl: yup.mixed().required("This document is required"),
};

export const documentUploadDealerSchema = yup.object(
  documentUploadDealerFields,
);

export const documentUploadAdminSchema = yup.object({
  ...documentUploadDealerFields,
  crifReportUrl: yup.mixed().required("This document is required"),
});

export const financierSelectionSchema = yup.object({
  selectedFinancier: yup.string().trim().required("Please select a financier"),
});

export const getStepSchema = (stepKey, role) => {
  switch (stepKey) {
    case "driverInformation":
      return driverInformationSchema;
    case "assetAllocation":
      return assetAllocationSchema;
    case "documentUpload":
      return role === ROLES.ADMIN
        ? documentUploadAdminSchema
        : documentUploadDealerSchema;
    case "financierSelection":
      return financierSelectionSchema;
    default:
      return yup.object();
  }
};

export const validateStepData = async (stepKey, data, role, context = {}) => {
  const schema = getStepSchema(stepKey, role);

  try {
    await schema.validate(data, { abortEarly: false, context });
    return { isValid: true, errors: {} };
  } catch (err) {
    const errors = {};
    (err.inner || []).forEach((innerError) => {
      if (innerError.path && !errors[innerError.path]) {
        errors[innerError.path] = innerError.message;
      }
    });
    return { isValid: false, errors };
  }
};

export const isPhoneNumberUnique = async (phoneNumber) => {
  if (!PHONE_REGEX.test(phoneNumber || "")) return true;

  const { data } = await axios.get(endpoints.checkDriverFieldUniqueness, {
    params: { phoneNumber },
  });
  return !data?.userPresent;
};
